// Utility functions for cardio pace calculations and formatting

// Conversion factors to miles
const CONVERSION_FACTORS = {
  miles: 1,
  km: 0.621371,
  meters: 0.000621371,
  yards: 0.000568182
};

/**
 * Convert distance to miles
 * @param {number} distance - The distance value
 * @param {string} unit - The distance unit ('miles', 'km', 'meters', 'yards')
 * @returns {number} - Distance in miles
 */
export function convertToMiles(distance, unit) {
  if (distance === null || distance === undefined) {
    return 0;
  }
  const distanceNum = Number(distance);
  if (isNaN(distanceNum) || distanceNum === 0) {
    return 0;
  }
  const factor = CONVERSION_FACTORS[unit] || 1;
  return distanceNum * factor;
}

/**
 * Calculate pace in seconds per mile
 * @param {number} distance - Distance in any unit
 * @param {string} distanceUnit - The distance unit
 * @param {number} durationSeconds - Total duration in seconds
 * @returns {number|null} - Pace in seconds per mile, or null if invalid
 */
export function calculatePace(distance, distanceUnit, durationSeconds) {
  if (!distance || !durationSeconds) {
    return null;
  }

  const durationNum = Number(durationSeconds);
  if (isNaN(durationNum) || durationNum === 0) {
    return null;
  }

  const distanceInMiles = convertToMiles(distance, distanceUnit);
  if (distanceInMiles === 0) {
    return null;
  }

  // Pace = total seconds / miles
  return durationNum / distanceInMiles;
}

/**
 * Format pace in seconds to MM:SS string
 * @param {number} paceSeconds - Pace in seconds per mile
 * @returns {string} - Formatted pace string (e.g., "8:30")
 */
export function formatPace(paceSeconds) {
  if (paceSeconds === null || paceSeconds === undefined) {
    return 'N/A';
  }

  const paceNum = Number(paceSeconds);
  if (isNaN(paceNum) || paceNum === 0) {
    return 'N/A';
  }

  const minutes = Math.floor(paceNum / 60);
  const seconds = Math.floor(paceNum % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Format duration in seconds to HH:MM:SS or MM:SS string
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted duration string
 */
export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) {
    return 'N/A';
  }

  const secondsNum = Number(seconds);
  if (isNaN(secondsNum)) {
    return 'N/A';
  }

  const hours = Math.floor(secondsNum / 3600);
  const minutes = Math.floor((secondsNum % 3600) / 60);
  const secs = Math.floor(secondsNum % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }
}

/**
 * Format distance with unit
 * @param {number} distance - Distance value
 * @param {string} unit - Distance unit
 * @returns {string} - Formatted distance string (e.g., "5.2 miles")
 */
export function formatDistance(distance, unit) {
  if (distance === null || distance === undefined) {
    return 'N/A';
  }
  // Convert to number in case it's a string from the database
  const distanceNum = Number(distance);
  if (isNaN(distanceNum)) {
    return 'N/A';
  }
  return `${distanceNum.toFixed(2)} ${unit}`;
}

/**
 * Convert pace in seconds per mile back to readable object
 * Used for sorting
 * @param {string} paceString - Pace string like "8:30"
 * @returns {number} - Pace in seconds
 */
export function paceStringToSeconds(paceString) {
  if (!paceString || paceString === 'N/A') {
    return Infinity; // Sort N/A to the bottom
  }

  const [minutes, seconds] = paceString.split(':').map(Number);
  return minutes * 60 + seconds;
}
